
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API_URL = `${process.env.REACT_APP_BASEURL}/product/dashboard/summary/`;


const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

export default function DashboardKpi() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(API_URL, {
        headers: { Authorization: `Token ${localStorage.getItem('adminAuth')}` },
      })
      .then((res) => {
        setData(res.data?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching dashboard:", err);
        setLoading(false);
      });
  }, []);

  if (loading) return <p className="p-6">Loading...</p>;
  if (!data) return <p className="p-6 text-red-500">No data found</p>;

  const { overview, products, users, orders, payments, inspections, activity } = data;

   
  return (
    <div className="p-6 space-y-6">
      {/* Top Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card title="Total Products" value={overview.total_products} />
        <Card title="Total Users" value={overview.total_users} />
        <Card title="Total Orders" value={overview.total_orders} />
        <Card title="Total Revenue" value={`₹${overview.total_revenue}`} />
      </div>

      {/* Products Section */}
      <Section title="Products">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChartCard title="By Type">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={Object.entries(products.by_type).map(([name, value]) => ({
                    name,
                    value,
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                  dataKey="value"
                >
                  {Object.entries(products.by_type).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="By Status">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={Object.entries(products.by_status).map(([name, value]) => ({
                  name,
                  value,
                }))}
              >
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Total Views: {products.total_views}
        </p>
      </Section>

      {/* Users Section */}
      <Section title="Users">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChartCard title="By Type">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={Object.entries(users.by_type).map(([key, val]) => ({
                    name: val.name,
                    value: val.count,
                  }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                  dataKey="value"
                >
                  {Object.entries(users.by_type).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
          <div className="grid grid-cols-2 gap-4">
            <Card title="Active Users" value={users.active} />
            <Card title="Verified Users" value={users.verified} />
          </div>
        </div>
      </Section>

      {/* Orders Section */}
      <Section title="Orders">
        <ChartCard title="By Status">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={Object.entries(orders.by_status).map(([name, value]) => ({
                name,
                value,
              }))}
            >
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Section>

      {/* Payments Section */}
      <Section title="Payments">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card title="Total Payments" value={payments.total_payments} />
          <Card title="Total Amount" value={`₹${payments.total_amount}`} />
          <Card title="Pending" value={payments.pending_payments} />
          <Card title="Completed" value={payments.completed_payments} />
        </div>
      </Section>

      {/* Inspections Section */}
      <Section title="Inspections">
        <div className="grid grid-cols-3 gap-4">
          <Card title="Pending" value={inspections.pending} />
          <Card title="Completed" value={inspections.completed} />
          <Card title="Canceled" value={inspections.canceled} />
        </div>
      </Section>

      {/* Activity Section */}
      <Section title="Recent Activity">
        <div className="grid grid-cols-3 gap-4">
          <Card title="Viewed Products" value={activity.recently_viewed_products} />
          <Card title="Users w/ Products" value={activity.users_with_products} />
          <Card title="Users w/ Orders" value={activity.users_with_orders} />
        </div>
      </Section>

      {/* Last Updated */}
      <p className="text-xs text-gray-400 text-right">
        Last updated: {new Date(data.timestamp).toLocaleString()}
      </p>
    </div>
  );
}

/* ---------- Reusable UI Components ---------- */

function Card({ title, value }) {
  return (
    <div className="p-4 bg-white shadow rounded-2xl text-center">
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-lg font-semibold mb-2">{title}</h2>
      {children}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="p-4 bg-white shadow rounded-2xl">
      <p className="text-sm text-gray-500 mb-2">{title}</p>
      {children}
    </div>
  );
}



// import React, { useEffect, useState } from "react";
// import styles from "../assets/css/dashboard.module.css";
// import { Bar } from "react-chartjs-2";
// import axios from "axios";
// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   BarElement,
//   Title,
//   Tooltip,
//   Legend,
// } from "chart.js";


// import {
//   rupee,
//   question,
//   user2,
//   // chart,
//   rupees,
//   shipping,
//   distributors,
//   inspections,
//   orders,
//   speciality,
// } from "../assets/img/index";

// // Register chart components
// ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);


// export const DashboardKpi = () => {
//   const [totalUsers, setTotalUsers] = useState(0);
//   const [totalOrders, setTotalOrders] = useState(0);
//   const [totalRequirementsReceived, setTotalRequirementsReceived] = useState(0);
//   const [totalProducts, setTotalProducts] = useState(0);
//   const [totalDistributorsManufacturers, setTotalDistributorsManufacturers] = useState(0);
//   const [totalSpecialities, setTotalSpecialities] = useState(0);
//   const [totalInspectionRequests, setTotalInspectionRequests] = useState(0);
//   const [totalInspection, setTotalInspection] = useState(0);
//   const [totalShippingRequests, setTotalShippingRequests] = useState(0);
//   const [totalEarnings, setTotalEarnings] = useState(0);

//   const [inspectionRequests, setInspectionRequests] = useState([]);
//   const [currentPage, setCurrentPage] = useState(1); // Track current page
//   const [postsPerPage] = useState(5); // Set number of posts per page

//   const token = localStorage.getItem("token");

//   useEffect(() => {
//     // Fetch Total Users
//     fetch("https://mmq.mediquip.in/api/v1/users/total_users/")
//       .then((response) => response.json())
//       .then((data) => setTotalUsers(data.total_users || 0))
//       .catch((error) => console.error("Error fetching total users:", error));

//     // Fetch Total Orders
//     fetch("https://mmq.mediquip.in/api/v1/product/order/order_count/")
//       .then((response) => response.json())
//       .then((data) => setTotalOrders(data.total_orders || 0))
//       .catch((error) => console.error("Error fetching total orders:", error));

//     // Fetch Total Requirements Received
//     fetch("https://mmq.mediquip.in/api/v1/product/total_requirements_received/")
//       .then((response) => response.json())
//       .then((data) => setTotalRequirementsReceived(data.total_requirements_received || 0))
//       .catch((error) => console.error("Error fetching total requirements:", error));

//     // Fetch Total Products
//     fetch("https://mmq.mediquip.in/api/v1/product/product_count/")
//       .then((response) => response.json())
//       .then((data) => setTotalProducts(data.total_products || 0))
//       .catch((error) => console.error("Error fetching total products:", error));

//     // Fetch Distributors and Manufacturer Count
//     fetch("https://mmq.mediquip.in/api/v1/product/get_dist_manu_count/")
//       .then((response) => response.json())
//       .then((data) => {
//         const totalCount = (data.distributor_count || 0) + (data.manufacturer_count || 0);
//         setTotalDistributorsManufacturers(totalCount);
//       })
//       .catch((error) => console.error("Error fetching distributors and manufacturers:", error));

//     // Fetch Total Specialities
//     fetch("https://mmq.mediquip.in/api/v1/product/speciality/get_speciality_count/")
//       .then((response) => response.json())
//       .then((data) => setTotalSpecialities(data.total_specialities || 0))
//       .catch((error) => console.error("Error fetching total specialities:", error));

//     // Fetch Total Inspection Requests
//     fetch("https://mmq.mediquip.in/api/v1/product/total_inspection_requests/")
//       .then((response) => response.json())
//       .then((data) => setTotalInspectionRequests(data.total_inspection_requests || 0))
//       .catch((error) => console.error("Error fetching total inspection requests:", error));

//     // Fetch Inspection Request Data
//     fetch("https://mmq.mediquip.in/api/v1/product/total_inspection/")
//       .then((response) => response.json())
//       .then((data) => setInspectionRequests(data.user_details || []))
//       .catch((error) => console.error("Error fetching inspection requests:", error));

//     // Fetch Total Shipping Requests
//     fetch("https://mmq.mediquip.in/api/v1/product/total_shipping_requests/")
//       .then((response) => response.json())
//       .then((data) => setTotalShippingRequests(data.total_shipping_requests || 0))
//       .catch((error) => console.error("Error fetching total shipping requests:", error));

//     // Fetch Total Earnings
//     fetch("https://mmq.mediquip.in/api/v1/product/order/total_earnings/")
//       .then((response) => response.json())
//       .then((data) => setTotalEarnings(data.total_earnings || 0))
//       .catch((error) => console.error("Error fetching total earnings:", error));
//   }, []);

//   // Get current posts
//   const indexOfLastPost = currentPage * postsPerPage;
//   const indexOfFirstPost = indexOfLastPost - postsPerPage;
//   const currentInspectionRequests = inspectionRequests.slice(indexOfFirstPost, indexOfLastPost);

//   // Change page
//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const kpiData = [
//     { title: "Total Product", img: rupee, fact: totalProducts },
//     { title: "Total Users", img: user2, fact: totalUsers },
//     { title: "Distributors and Manufacturer", img: distributors, fact: totalDistributorsManufacturers },
//     { title: "Total Requirement received", img: question, fact: totalRequirementsReceived },
//     { title: "Total Earning", img: rupees, fact: totalEarnings },
//     { title: "Inspection Request", img: inspections, fact: totalInspectionRequests },
//     { title: "Shipping Request", img: shipping, fact: totalShippingRequests },
//     { title: "Total Orders", img: orders, fact: totalOrders },
//     { title: "Total Speciality", img: speciality, fact: totalSpecialities },
//   ];

//   return (
//     <section className={styles.dashBoardAnalytics}>
//       <div className={styles.dashboardKpi}>
//         {kpiData.map((value, key) => (
//           <KPICard data={value} key={key} />
//         ))}
//       </div>

//       <div className={styles.AnalyticsData}>
//         <AnalyticsChart />
//         {/* <InspectionRequestData
//           inspectionRequests={currentInspectionRequests}
//           paginate={paginate}
//           totalInspectionRequests={totalInspectionRequests}
//         /> */}
//         {/* <ShippingRequestData totalShippingRequests={totalShippingRequests} /> */}
//       </div>
//     </section>
//   );
// };

// const KPICard = ({ data }) => {
//   return (
//     <div className={styles.dashboardKpiCard}>
//       <div className={styles.dashboardKpiCont}>
//         <div>
//           <span>
//             <img src={data.img} alt="..." />
//           </span>
//         </div>
//         <div>
//           <h4>{data.title}</h4>
//           <h1>{data.fact}</h1>
//         </div>
//       </div>
//     </div>
//   );
// };

// const AnalyticsChart = () => {
//   const [chartData, setChartData] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     axios
//       .get("https://mmq.mediquip.in/api/v1/users/user_analytics/")
//       .then((response) => {
//         const data = response.data;

//         // Map userTypeId to actual names
//         const userTypeNames = {
//           "1": "Distributor",
//           "2": "Manufacturer",
//           "3": "Normal",
//         };

//         // Prepare the datasets based on user types
//         const userTypes = {}; // Store user type data dynamically
//         data.data.forEach((entry) => {
//           Object.entries(entry.user_types).forEach(([userTypeId, count]) => {
//             const userTypeName = userTypeNames[userTypeId] || `User Type ${userTypeId}`; // Map the userTypeId to its name

//             if (!userTypes[userTypeId]) {
//               userTypes[userTypeId] = {
//                 label: userTypeName, // Use the mapped user type name
//                 data: new Array(data.labels.length).fill(0), // Initialize data for all years
//                 backgroundColor: "rgba(1, 156, 137, 1)", // Choose colors dynamically
//                 borderColor: "rgba(54, 162, 235, 1)",
//                 borderWidth: 1,
//               };
//             }

//             // Find the corresponding index for the year
//             const yearIndex = data.labels.indexOf(entry.year);
//             if (yearIndex !== -1) {
//               userTypes[userTypeId].data[yearIndex] = count; // Populate data for the year
//             }
//           });
//         });

//         // Convert the userTypes object to an array of datasets
//         const datasets = Object.values(userTypes);

//         setChartData({
//           labels: data.labels, // ["2024", "2023"]
//           datasets: datasets, // Multiple datasets based on user types
//         });

//         setLoading(false);
//       })
//       .catch((error) => {
//         console.error("Error fetching user analytics data:", error);
//         setLoading(false);
//       });
//   }, []);

//   if (loading) {
//     return <p>Loading chart...</p>;
//   }

//   return (
//     <div className="w-full max-w-3xl mx-auto p-4 bg-white shadow-lg rounded-lg">
//       <h2 className="text-lg font-bold text-center mb-4">User Analytics by Year</h2>
//       {chartData && <Bar data={chartData} />}
//     </div>
//   );
// };

// export default AnalyticsChart;
